import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";

// =============================================================================
// SitesMapWidget — Australia map with site dots colour-coded by project status.
// T-071 Phase A continuation per James 2026-04-25.
//
// The coastline is built from REAL coastal lat/lng landmarks projected through
// the same linear projection the site dots use — so the outline is correctly
// proportioned AND dots always sit right against the coast. Points are then
// Catmull-Rom smoothed into a natural curve.
// =============================================================================

type ProjectStatus =
  | "bid" | "quoted" | "won" | "mobilising" | "live"
  | "on_hold" | "acceptance_pending" | "closed" | "cancelled" | "lost";

type DotColour = "active" | "acceptance" | "bid" | "built" | "none";

const COLOUR_HEX: Record<DotColour, string> = {
  active:     "#3B82F6",  // blue — live / mobilising
  acceptance: "#F59E0B",  // gold — acceptance pending
  bid:        "#EF4444",  // red — open BID / quoted
  built:      "#10B981",  // green — closed
  none:       "#6B7280",  // grey — no projects
};

const COLOUR_LABEL: Record<DotColour, string> = {
  active:     "Live / mobilising",
  acceptance: "Acceptance pending",
  bid:        "BID / quoted",
  built:      "Built (closed)",
  none:       "No projects",
};

interface SiteRow {
  site_id:      string;
  site_code:    string;
  site_name:    string;
  suburb:       string | null;
  state:        string | null;
  latitude:     number;
  longitude:    number;
  projects:     ProjEntry[];
}

interface ProjEntry {
  project_id: string;
  status:     ProjectStatus;
  client_id:  string | null;
  date:       string | null;
}

// Linear lat/lng → SVG x/y projection. Good enough at country scale.
const LNG_MIN = 110, LNG_MAX = 156;
const LAT_MAX = -9,  LAT_MIN = -44;
const VIEW_W  = 1000, VIEW_H = 800;

function project(lat: number, lng: number): { x: number; y: number } {
  const x = ((lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * VIEW_W;
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * VIEW_H;
  return { x, y };
}

// Nudge a projected point slightly toward the centre of the country so coastal
// sites sit ON the landmass rather than in the water. Tiny + capped — invisible
// for inland sites, just enough to pull a coast-hugging dot off the edge.
const CENTROID = { x: 529, y: 379 };  // ~ geographic centre of Australia in SVG space
function onLand(p: { x: number; y: number }): { x: number; y: number } {
  const dx = CENTROID.x - p.x, dy = CENTROID.y - p.y;
  const dist = Math.hypot(dx, dy) || 1;
  const nudge = Math.min(dist * 0.045, 9);
  return { x: p.x + (dx / dist) * nudge, y: p.y + (dy / dist) * nudge };
}

// Mainland coast — real [lat, lng] landmarks, clockwise from Cape York tip.
const AUS_COORDS: [number, number][] = [
  [-10.70, 142.53], // Cape York tip
  [-12.80, 143.55], // Lockhart River (E Cape York)
  [-14.17, 144.50], // Cape Melville
  [-15.47, 145.25], // Cooktown
  [-16.92, 145.77], // Cairns
  [-18.27, 146.02], // Cardwell / Hinchinbrook
  [-19.26, 146.82], // Townsville
  [-20.01, 148.25], // Bowen / Whitsundays
  [-21.14, 149.19], // Mackay
  [-22.35, 150.00], // Shoalwater
  [-23.13, 150.74], // Yeppoon
  [-23.84, 151.26], // Gladstone
  [-24.85, 152.35], // Bundaberg
  [-24.72, 153.25], // Fraser Island (Sandy Cape)
  [-26.40, 153.10], // Noosa
  [-27.40, 153.40], // Brisbane / Moreton
  [-28.00, 153.43], // Gold Coast
  [-28.64, 153.63], // Byron Bay (easternmost)
  [-29.43, 153.36], // Yamba
  [-30.30, 153.14], // Coffs Harbour
  [-31.43, 152.92], // Port Macquarie
  [-32.18, 152.52], // Forster
  [-32.93, 151.79], // Newcastle
  [-33.87, 151.28], // Sydney
  [-34.42, 150.90], // Wollongong
  [-35.12, 150.76], // Jervis Bay
  [-35.71, 150.18], // Batemans Bay
  [-37.07, 149.90], // Eden
  [-37.50, 149.97], // Cape Howe
  [-37.80, 148.53], // Marlo
  [-37.88, 147.98], // Lakes Entrance
  [-39.13, 146.42], // Wilsons Prom (southernmost mainland)
  [-38.47, 145.30], // Western Port
  [-38.30, 144.62], // Port Phillip Heads
  [-38.86, 143.51], // Cape Otway
  [-38.40, 142.48], // Warrnambool
  [-38.35, 141.61], // Portland
  [-38.05, 140.70], // Port MacDonnell
  [-36.82, 139.85], // Kingston SE
  [-35.90, 139.35], // Coorong
  [-35.55, 138.87], // Murray Mouth
  [-35.61, 138.09], // Cape Jervis
  [-35.22, 136.92], // Yorke Pen (Marion Bay)
  [-33.60, 137.62], // Cowell (Spencer Gulf E)
  [-32.49, 137.77], // Port Augusta (top of Spencer Gulf)
  [-33.20, 137.00], // Whyalla / Spencer Gulf W
  [-34.72, 135.86], // Port Lincoln (Eyre tip)
  [-33.65, 134.89], // Elliston
  [-32.80, 134.21], // Streaky Bay
  [-32.13, 133.67], // Ceduna
  [-31.48, 131.13], // Head of Bight
  [-31.68, 128.88], // Eucla
  [-33.60, 123.88], // Israelite Bay (Bight W)
  [-33.86, 121.89], // Esperance
  [-34.40, 119.38], // Bremer Bay
  [-35.02, 117.88], // Albany
  [-34.98, 116.73], // Walpole
  [-34.37, 115.14], // Cape Leeuwin
  [-33.54, 115.02], // Cape Naturaliste
  [-32.53, 115.72], // Mandurah
  [-32.05, 115.74], // Fremantle / Perth
  [-31.02, 115.33], // Lancelin
  [-30.31, 115.04], // Jurien Bay
  [-28.77, 114.61], // Geraldton
  [-27.71, 114.16], // Kalbarri
  [-26.15, 113.15], // Steep Point (westernmost)
  [-24.88, 113.66], // Carnarvon
  [-23.14, 113.77], // Coral Bay
  [-21.82, 114.10], // NW Cape (Exmouth)
  [-21.64, 115.11], // Onslow
  [-20.66, 116.77], // Karratha / Dampier
  [-20.31, 118.58], // Port Hedland
  [-19.50, 121.00], // Eighty Mile Beach
  [-17.96, 122.24], // Broome
  [-16.40, 122.92], // Cape Leveque
  [-17.30, 123.62], // Derby (King Sound)
  [-15.48, 124.52], // Kuri Bay
  [-14.30, 126.63], // Kalumburu
  [-15.45, 128.12], // Wyndham (Cambridge Gulf)
  [-14.25, 129.53], // Port Keats (JB Gulf)
  [-12.46, 130.84], // Darwin
  [-11.30, 131.77], // Cobourg Peninsula
  [-12.05, 134.23], // Maningrida
  [-12.19, 136.78], // Nhulunbuy / Gove
  [-14.27, 135.72], // Numbulwar (W Gulf of Carpentaria)
  [-16.07, 136.31], // Borroloola
  [-17.10, 138.30], // SW Gulf (Burketown)
  [-17.48, 140.84], // Karumba (bottom of Gulf)
  [-14.90, 141.61], // Pormpuraaw (E Gulf)
  [-12.63, 141.87], // Weipa
  [-11.20, 142.05], // Mapoon (W Cape York)
];

// Tasmania — real [lat, lng] landmarks, clockwise from NW.
const TAS_COORDS: [number, number][] = [
  [-40.68, 144.71], // Cape Grim (NW)
  [-40.77, 145.30], // Stanley
  [-41.18, 146.36], // Devonport
  [-40.98, 147.60], // Bridport
  [-40.75, 147.96], // Cape Portland (NE)
  [-41.30, 148.29], // St Helens (E)
  [-41.88, 148.30], // Bicheno
  [-43.05, 147.90], // Tasman Peninsula
  [-43.64, 146.83], // South East Cape (southernmost)
  [-43.56, 146.00], // South West Cape
  [-42.20, 145.16], // Cape Sorell (W)
  [-41.20, 144.70], // NW coast up to Cape Grim
];

// Catmull-Rom → cubic-bezier smoother for a closed loop of projected points.
function coastToPath(coords: [number, number][]): string {
  const pts = coords.map(([lat, lng]) => project(lat, lng));
  const n = pts.length;
  if (n < 3) return "";
  const f = (v: number) => v.toFixed(1);
  let d = `M ${f(pts[0].x)},${f(pts[0].y)} `;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += `C ${f(c1x)},${f(c1y)} ${f(c2x)},${f(c2y)} ${f(p2.x)},${f(p2.y)} `;
  }
  return d + "Z";
}

const AUSTRALIA_PATH = coastToPath(AUS_COORDS);
const TASMANIA_PATH  = coastToPath(TAS_COORDS);

// Project status → DotColour priority bucket
function statusBucket(status: ProjectStatus): DotColour {
  if (status === "mobilising" || status === "live")  return "active";
  if (status === "acceptance_pending")               return "acceptance";
  if (status === "bid" || status === "quoted")       return "bid";
  if (status === "closed")                           return "built";
  if (status === "won" || status === "on_hold")      return "active";
  return "none";
}

const PRIORITY: Record<DotColour, number> = { active: 5, acceptance: 4, bid: 3, built: 2, none: 1 };

function siteColour(projects: SiteRow["projects"]): DotColour {
  if (projects.length === 0) return "none";
  let best: DotColour = "none";
  for (const p of projects) {
    const c = statusBucket(p.status);
    if (PRIORITY[c] > PRIORITY[best]) best = c;
  }
  return best;
}

// -----------------------------------------------------------------------------

export default function SitesMapWidget() {
  const { data, isLoading } = useQuery({
    queryKey: ["sites_map_widget"],
    queryFn: async () => {
      const [sitesRes, projectsRes, bidsRes, clientsRes] = await Promise.all([
        supabase.from("sites")
          .select("site_id, site_code, site_name, suburb, state, latitude, longitude")
          .not("latitude",  "is", null)
          .not("longitude", "is", null)
          .eq("country", "AU"),
        supabase.from("projects")
          .select("project_id, status, site_id, client_id, updated_at")
          .not("site_id", "is", null),
        supabase.from("bids")
          .select("bid_id, site_id, client_id, created_at")
          .eq("status", "open")
          .not("site_id", "is", null),
        supabase.from("clients")
          .select("client_id, legal_name"),
      ]);

      const clientName = new Map<string, string>();
      for (const c of (clientsRes.data ?? []) as Array<{ client_id: string; legal_name: string }>) {
        clientName.set(c.client_id, c.legal_name);
      }

      const bySite = new Map<string, ProjEntry[]>();
      for (const p of (projectsRes.data ?? []) as any[]) {
        const arr = bySite.get(p.site_id) ?? [];
        arr.push({ project_id: p.project_id, status: p.status, client_id: p.client_id ?? null, date: p.updated_at ?? null });
        bySite.set(p.site_id, arr);
      }
      for (const b of (bidsRes.data ?? []) as any[]) {
        const arr = bySite.get(b.site_id) ?? [];
        arr.push({ project_id: `bid:${b.bid_id}`, status: "bid", client_id: b.client_id ?? null, date: b.created_at ?? null });
        bySite.set(b.site_id, arr);
      }

      const sites = ((sitesRes.data ?? []) as Array<Omit<SiteRow, "projects">>)
        .map(s => ({ ...s, projects: bySite.get(s.site_id) ?? [] }))
        .filter(s => s.projects.length > 0) as SiteRow[];

      const clientIds = new Set<string>();
      for (const s of sites) for (const p of s.projects) if (p.client_id) clientIds.add(p.client_id);
      const clients = Array.from(clientIds)
        .map(id => ({ client_id: id, name: clientName.get(id) ?? "—" }))
        .sort((a, b) => a.name.localeCompare(b.name));

      return { sites, clients };
    },
  });

  const sites   = data?.sites   ?? [];
  const clients = data?.clients ?? [];

  const [hoverSiteId, setHoverSiteId] = useState<string | null>(null);
  const [range,    setRange]    = useState<"all" | "6mo">("all");
  const [clientId, setClientId] = useState<string>("");

  const sixMonthsAgo = useMemo(() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return d.getTime(); }, []);
  const filteredSites = useMemo(() => {
    if (range === "all" && !clientId) return sites;
    return sites
      .map(s => ({
        ...s,
        projects: s.projects.filter(p =>
          (!clientId || p.client_id === clientId) &&
          (range === "all" || (p.date != null && new Date(p.date).getTime() >= sixMonthsAgo))
        ),
      }))
      .filter(s => s.projects.length > 0);
  }, [sites, range, clientId, sixMonthsAgo]);

  const counts = useMemo(() => {
    const c = { active: 0, acceptance: 0, bid: 0, built: 0, none: 0 };
    for (const s of filteredSites) c[siteColour(s.projects)]++;
    return c;
  }, [filteredSites]);

  const hoverSite = filteredSites.find(s => s.site_id === hoverSiteId) ?? null;

  return (
    <div className="relative">
      {/* Filter bar — time window + client */}
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-line overflow-hidden">
          <button
            type="button"
            onClick={() => setRange("all")}
            className={`px-3 py-1.5 text-[12px] font-medium ${range === "all" ? "bg-accent text-white" : "bg-ink text-text-muted hover:text-text"}`}
          >All sites</button>
          <button
            type="button"
            onClick={() => setRange("6mo")}
            className={`px-3 py-1.5 text-[12px] font-medium border-l border-line ${range === "6mo" ? "bg-accent text-white" : "bg-ink text-text-muted hover:text-text"}`}
          >Past 6 months</button>
        </div>
        <select
          value={clientId}
          onChange={e => setClientId(e.target.value)}
          className="px-2.5 py-1.5 text-[12px] rounded-lg border border-line bg-ink text-text"
        >
          <option value="">All clients</option>
          {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
        </select>
        {(range !== "all" || clientId) && (
          <button
            type="button"
            onClick={() => { setRange("all"); setClientId(""); }}
            className="px-2.5 py-1.5 text-[12px] text-text-muted hover:text-accent"
          >Clear</button>
        )}
        <span className="ml-auto text-[11px] text-text-muted font-mono">
          {filteredSites.length} site{filteredSites.length === 1 ? "" : "s"}
        </span>
      </div>

      <div
        className="relative w-full mx-auto"
        style={{ maxWidth: 1000, maxHeight: 640 }}
      >
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
          style={{ maxHeight: 640 }}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Map of Australia with site locations"
        >
          {/* Country outline */}
          <path
            d={AUSTRALIA_PATH}
            fill="rgb(38, 38, 45)"
            stroke="rgb(82, 82, 95)"
            strokeWidth={1.5}
            strokeLinejoin="round"
          />
          <path
            d={TASMANIA_PATH}
            fill="rgb(38, 38, 45)"
            stroke="rgb(82, 82, 95)"
            strokeWidth={1.5}
            strokeLinejoin="round"
          />

          {/* Site dots */}
          {filteredSites.map(s => {
            const { x, y } = onLand(project(s.latitude, s.longitude));
            const colour = siteColour(s.projects);
            const isHover = s.site_id === hoverSiteId;
            return (
              <g key={s.site_id}>
                {isHover && (
                  <circle
                    cx={x} cy={y} r={18}
                    fill={COLOUR_HEX[colour]}
                    fillOpacity={0.2}
                    className="pointer-events-none"
                  />
                )}
                <circle
                  cx={x} cy={y}
                  r={isHover ? 9 : 7}
                  fill={COLOUR_HEX[colour]}
                  stroke="rgb(15, 15, 20)"
                  strokeWidth={2}
                  className="cursor-pointer transition-all"
                  onMouseEnter={() => setHoverSiteId(s.site_id)}
                  onMouseLeave={() => setHoverSiteId(null)}
                />
              </g>
            );
          })}
        </svg>

        {/* Hover tooltip */}
        {hoverSite && (() => {
          const { x, y } = onLand(project(hoverSite.latitude, hoverSite.longitude));
          const leftPct = (x / VIEW_W) * 100;
          const topPct  = (y / VIEW_H) * 100;
          const projects = hoverSite.projects;
          return (
            <div
              className="absolute pointer-events-none bg-ink-raised border border-line rounded shadow-xl p-2 text-[11px] z-10"
              style={{
                left: `${leftPct}%`,
                top:  `${topPct}%`,
                transform: "translate(-50%, calc(-100% - 14px))",
                minWidth: 160, maxWidth: 240,
              }}
            >
              <div className="text-text font-medium truncate">{hoverSite.site_name}</div>
              <div className="text-[10px] font-mono text-text-muted mt-0.5">
                {hoverSite.site_code}
                {hoverSite.suburb && <> · {hoverSite.suburb}</>}
                {hoverSite.state  && <>, {hoverSite.state}</>}
              </div>
              {projects.length > 0 ? (
                <div className="mt-1.5 text-[10px] text-text-muted">
                  {projects.length} project{projects.length === 1 ? "" : "s"}
                  {" — "}
                  <span style={{ color: COLOUR_HEX[siteColour(projects)] }}>
                    {COLOUR_LABEL[siteColour(projects)]}
                  </span>
                </div>
              ) : (
                <div className="mt-1.5 text-[10px] text-text-dim italic">No projects yet</div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Legend */}
      <div className="mt-4 p-3 rounded bg-ink border border-line">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap text-[12px]">
            <LegendChip colour="active"     count={counts.active} />
            <LegendChip colour="acceptance" count={counts.acceptance} />
            <LegendChip colour="bid"        count={counts.bid} />
            <LegendChip colour="built"      count={counts.built} />
            {filteredSites.length === 0 && (
              <span className="text-text-muted italic">
                {sites.length === 0 ? "No projects on the map yet" : "No sites match this filter"}
              </span>
            )}
          </div>
          <Link to="/sites" className="text-text-muted hover:text-accent text-[12px] inline-flex items-center gap-1 font-medium">
            View all sites <ChevronRight size={12}/>
          </Link>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function LegendChip({ colour, count }: { colour: DotColour; count: number }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span
        className="w-3.5 h-3.5 rounded-full border-2 flex-shrink-0"
        style={{ background: COLOUR_HEX[colour], borderColor: "rgb(15, 15, 20)" }}
      />
      <span className="text-text">{COLOUR_LABEL[colour]}</span>
      <span className="font-mono text-text-muted">·</span>
      <span className="font-mono text-text font-semibold">{count}</span>
    </div>
  );
}
